import { useMemo } from "react";
import { Check } from "@phosphor-icons/react";
import { pillarStyle, getHorizons, normalizeSteps } from "@/lib/planiq";
import { stripMd } from "@/lib/stripMd";
import { PageSheet } from "./PageParts";

const PLAN_LABEL = {
  thirty_sixty_ninety: "30-60-90",
  tactical_sprint: "Tactical Sprint",
  macro_vision: "Macro Vision",
};

// Mirror the backend ~90-word ceiling so the on-screen preview matches the PDF cover.
const capWords = (s, n = 250) => {
  const w = (s || "").split(/\s+/).filter(Boolean);
  if (w.length <= n) return s || "";
  const clip = w.slice(0, n).join(" ");
  const cut = Math.max(clip.lastIndexOf(". "), clip.lastIndexOf("! "), clip.lastIndexOf("? "));
  return cut > clip.length * 0.5 ? clip.slice(0, cut + 1) : clip.replace(/[,;:\s]+$/, "") + ".";
};

// The board-ready plan document, mirroring the PDF 1:1 on discrete A4 sheets:
// cover (summary + horizon + focus areas) → "Plan on a Page" matrix → one printable
// checklist per deliverable → appendix.
export default function DocumentBody({ plan, cards, pillars, notes = true }) {
  const pillarById = Object.fromEntries(pillars.map((p) => [p.id, p]));
  const horizons = getHorizons(plan.plan_type);
  const horizonLabel = Object.fromEntries(horizons.map((h) => [h.key, h.label]));
  const matrixCols = "1.4fr " + horizons.map(() => "1fr").join(" ");
  const planLabel = PLAN_LABEL[plan.plan_type] || PLAN_LABEL.thirty_sixty_ninety;
  const constraints = plan.blueprint?.inferred_constraints || {};
  const hazards = constraints.operational_hazards || [];
  const friction = constraints.cultural_friction || [];
  const limits = constraints.resource_limits || [];

  const orderedCards = useMemo(() => {
    const rank = Object.fromEntries(horizons.map((h, i) => [h.key, i]));
    return [...cards].sort(
      (a, b) => (rank[a.horizon] ?? 99) - (rank[b.horizon] ?? 99) || (a.order ?? 0) - (b.order ?? 0)
    );
  }, [cards, horizons]);

  const pages = useMemo(() => {
    const out = [{ kind: "summary" }, { kind: "matrix" }];
    orderedCards.forEach((c) => out.push({ kind: "checklist", card: c }));
    out.push({ kind: "appendix" });
    return out;
  }, [orderedCards]);

  const totalPages = pages.length;

  const renderSummary = () => (
    <div className="pq-cover">
      <p className="text-[11px] tracking-[0.32em] uppercase font-bold text-gray-500">Strategic Plan · {planLabel}</p>
      <h1 className="font-display font-black text-5xl tracking-tight mt-3 leading-[1.04]">{plan.title}</h1>
      <div className="h-px bg-gray-950 mt-7 mb-9" />
      <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Executive Summary</h2>
      {plan.executive_summary ? (
        <p className="text-[15px] text-gray-700 leading-[1.8] whitespace-pre-wrap max-w-2xl">{stripMd(capWords(plan.executive_summary))}</p>
      ) : (
        <p className="text-sm text-gray-400 italic font-mono">
          No summary yet. Click "Generate Summary" to create a clear overview.
        </p>
      )}
      <div className="mt-8 border-t border-gray-200 pt-6 max-w-2xl">
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold mb-1.5">Structured over</p>
        <p className="text-[15px] text-gray-800 mb-6">{horizons.map((h) => h.label).join("   ·   ")}</p>
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold mb-2.5">Focus areas</p>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {pillars.map((p) => {
            const s = pillarStyle(p);
            return (
              <span key={p.id} className="inline-flex items-center gap-2 text-sm text-gray-700">
                <span className="w-2.5 h-2.5" style={{ backgroundColor: s.hex }} />
                {p.name}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderMatrix = () => (
    <>
      <h2 className="font-display font-bold text-2xl tracking-tight">Plan on a Page</h2>
      <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-400 mb-5">Key deliverables by focus area, across the plan</p>
      <div className="border border-gray-300 overflow-hidden" data-testid="strategic-matrix">
        <div className="grid" style={{ gridTemplateColumns: matrixCols }}>
          <div className="bg-gray-950 text-white text-xs font-bold uppercase tracking-wider px-3 py-2.5">Pillar</div>
          {horizons.map((h) => (
            <div key={h.key} className="bg-gray-950 text-white text-xs font-bold uppercase tracking-wider px-3 py-2.5 border-l border-gray-700">
              {h.label}
            </div>
          ))}
          {pillars.map((p, i) => {
            const style = pillarStyle(p);
            return (
              <div key={p.id} className="contents">
                <div className={`px-3 py-3 text-sm font-semibold border-t border-gray-200 flex items-center gap-2 ${i % 2 ? "bg-gray-50" : "bg-white"}`}>
                  <span className="w-2.5 h-2.5" style={{ backgroundColor: style.hex }} />
                  {p.name}
                </div>
                {horizons.map((h) => {
                  const cell = cards.filter((c) => c.pillar_id === p.id && c.horizon === h.key);
                  return (
                    <div key={h.key} className={`px-3 py-3 border-t border-l border-gray-200 ${i % 2 ? "bg-gray-50" : "bg-white"}`}>
                      {cell.length === 0 ? (
                        <span className="text-gray-300 font-mono text-xs">—</span>
                      ) : (
                        <ul className="space-y-1">
                          {cell.map((c) => (
                            <li key={c._id} className="text-xs text-gray-700 leading-snug">• {stripMd(c.title)}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );

  const renderChecklist = (card) => {
    const p = pillarById[card.pillar_id];
    const s = pillarStyle(p);
    let steps = normalizeSteps(card.steps);
    if (steps.length === 0 && card.execution_guide) {
      steps = card.execution_guide
        .split(/[\n\r]+|(?<=[.!?])\s+(?=[A-Z0-9])/)
        .map((t) => t.replace(/^[\s\-•*]+/, "").trim())
        .filter((t) => t.length > 3)
        .map((t) => ({ text: t, done: false }));
    }
    return (
      <div data-testid={`checklist-${card._id}`} className="flex flex-col flex-1">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em]" style={{ color: s.hex }}>
            <span className="w-2.5 h-2.5" style={{ backgroundColor: s.hex }} />
            {p?.name || "Unassigned"}
          </span>
          <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-gray-400">{horizonLabel[card.horizon]}</span>
        </div>
        <div className="h-[3px] w-12 my-4" style={{ backgroundColor: s.hex }} />
        <h2 className="font-display font-bold text-3xl tracking-tight leading-tight">{stripMd(card.title)}</h2>
        {card.justification && (
          <p className="text-base text-gray-600 leading-relaxed mt-3 mb-8 max-w-2xl">{stripMd(card.justification)}</p>
        )}
        <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold mb-1">Checklist</p>
        <ul>
          {steps.map((st, i) => (
            <li key={i} className="flex items-start gap-3 py-2.5 border-b border-gray-100">
              <span className={`mt-0.5 w-4 h-4 border shrink-0 flex items-center justify-center ${st.done ? "bg-gray-950 border-gray-950" : "border-gray-400 bg-white"}`}>
                {st.done && <Check size={11} weight="bold" className="text-white" />}
              </span>
              <span className={`text-[15px] leading-snug ${st.done ? "line-through text-gray-400" : "text-gray-800"}`}>
                {st.text}
              </span>
            </li>
          ))}
          {steps.length === 0 && <li className="text-sm text-gray-400 italic py-2">No checklist items.</li>}
        </ul>
        {notes && (
          <div className="flex-1 flex flex-col mt-8 min-h-[90px]" data-testid={`notes-area-${card._id}`}>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gray-400 font-bold mb-2.5">Notes</p>
            <div className="flex-1 min-h-[60px] border-t border-gray-200"
              style={{ backgroundImage: "repeating-linear-gradient(to bottom, transparent 0, transparent 27px, #e5e7eb 27px, #e5e7eb 28px)" }} />
          </div>
        )}
      </div>
    );
  };

  const renderAppendix = () => {
    const ROWS = [
      ["Operational Hazards & Systemic Bottlenecks", hazards],
      ["Cultural Friction & Stakeholder Realities", friction],
      ["Resource Limits", limits],
    ];
    return (
      <>
        <h2 className="font-display font-bold text-2xl tracking-tight">Context &amp; Hazard Appendix</h2>
        <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-400 mb-1">The constraints that shaped this plan</p>
        <div className="border-t-2 border-gray-950">
          {ROWS.map(([heading, items]) => (
            <section key={heading} className="py-4 border-b border-gray-200">
              <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-gray-950 mb-2.5">{heading}</h3>
              {items.length ? (
                <ul className="space-y-1.5">
                  {items.map((x, i) => (
                    <li key={i} className="text-sm text-gray-700 leading-relaxed flex gap-2"><span className="text-gray-400 shrink-0">—</span><span>{x}</span></li>
                  ))}
                </ul>
              ) : <p className="text-xs text-gray-400 italic">None recorded.</p>}
            </section>
          ))}
        </div>
      </>
    );
  };

  return (
    <div id="print-root" className="print-full w-full flex flex-col items-center gap-10 py-12 px-4">
      {pages.map((pg, i) => {
        const pageNum = i + 1;
        if (pg.kind === "summary") {
          return (
            <PageSheet key="summary" pageNum={pageNum} total={totalPages} title={plan.title} breakClass="pdf-page-first pdf-avoid">
              {renderSummary()}
            </PageSheet>
          );
        }
        if (pg.kind === "matrix") {
          return (
            <PageSheet key="matrix" pageNum={pageNum} total={totalPages} title={plan.title} landscape breakClass="pdf-page pdf-avoid">
              {renderMatrix()}
            </PageSheet>
          );
        }
        if (pg.kind === "appendix") {
          return (
            <PageSheet key="appendix" pageNum={pageNum} total={totalPages} title={plan.title} breakClass="pdf-page pdf-avoid">
              {renderAppendix()}
            </PageSheet>
          );
        }
        return (
          <PageSheet key={`cl-${pg.card._id}`} pageNum={pageNum} total={totalPages} title={plan.title} breakClass="pdf-page pdf-avoid">
            {renderChecklist(pg.card)}
          </PageSheet>
        );
      })}
    </div>
  );
}
